import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../state'

/**
 * Modal component for selecting payrolls for an employee occupant.
 *
 * This modal displays a list of payrolls for a selected employee, one per
 * project and organization, and allows the user to check/uncheck them to
 * allocate them to the occupant. Checking one allocates every payroll entry it
 * groups.
 *
 * Usage: This modal is opened after an edit button is clicked on an occupant,
 * and displays the payrolls available for that occupant's employee. Users can
 * allocate which payrolls should be relevant for this occupant.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.input.checkbox.Component,
    ds.layout.widget.Component,
    ds.notice.Component,
    ds.notice.icon.Component,
    ds.overlay.centered.Component,
    ngCommon.NgFor,
    ngCommon.PercentPipe,
  ],
  selector: 'atn-occupant-payrolls-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** The occupants entity collection */
  public readonly ecSg = ngCore.input.required<
    | state.space.full.recordOccupants.Collection
    | state.space.full.clusterOccupants.Collection
  >({ alias: 'collection' })

  /**
   * @see {@link ngCommon.PercentPipe}
   */
  public readonly percentPipe = ngCore.inject(ngCommon.PercentPipe)

  /** @see {@link ents.core.display.Style} */
  public readonly DisplayStyle = ents.core.display.Style

  /** @see {@link ents.extra.space.Record.Occupant.Payroll.Assignment} */
  public readonly Assignment =
    ents.extra.space.Record.Occupant.Payroll.Assignment

  /** Get payrolls from the updated occupant */
  public readonly payrollsSg = ngCore.computed(
    (
      occupant = this.ecSg()
        .plugins.currentEntityPlugin.feature.selectors.getUpdatedEntity()
        .asSignal(),
    ) => occupant?.payrolls ?? [],
  )

  /**
   * Whether any payroll is still only partially assigned, which must be
   * resolved to all or none of its entries before saving
   */
  public readonly hasPartiallyAssignedPayrollsSg = ngCore.computed(() =>
    this.payrollsSg().some(
      ({ assignment }) =>
        assignment ===
        ents.extra.space.Record.Occupant.Payroll.Assignment.Partial,
    ),
  )

  /**
   * Helper to format IDC allocation display for a payroll. Shows category or
   * subcategory and percent.
   */
  public readonly payrollAllocation = (
    payroll: state.space.full.recordOccupants.Entity.Payroll,
  ): string =>
    payroll.idcAllocations
      .map(({ idcCategorization: { category, subcategory }, percent }) => {
        const label = subcategory
          ? `${category.code} (${subcategory.code})`
          : category.code
        return `${label}: ${this.percentPipe.transform(percent)}`
      })
      .join(', ')

  /**
   * Handle checkbox state changes for individual payrolls, found by their
   * index, since a payroll groups many payroll entries and has no id of its own
   *
   * Changing a partially assigned payroll resolves it: it's then allocated with
   * all of its entries or none of them
   */
  public payrollCheckboxChangedHandler = (
    index: number,
    isSelected: boolean,
  ): void => {
    this.ecSg()
      .plugins.editingPlugin.feature.actions.edit.from(
        'PayrollCheckbox.Changed',
      )
      .dispatch({
        edit: occupant => {
          if (occupant) {
            const { payrolls } = occupant as FlattenUnion<typeof occupant>
            const payroll = payrolls?.[index]
            if (payroll) {
              payroll.assignment = isSelected
                ? ents.extra.space.Record.Occupant.Payroll.Assignment.Full
                : ents.extra.space.Record.Occupant.Payroll.Assignment.None
            }
          }
        },
      })
  }

  /**
   * Save handler - apply payroll updates, unless a payroll is still partially
   * assigned, since saving would silently unassign its assigned entries
   */
  public saveClickHandler = (): void => {
    if (this.hasPartiallyAssignedPayrollsSg()) {
      return
    }
    this.ecSg()
      .plugins.editingPlugin.feature.actions.submit.from(
        'Dialog.SaveButton.Clicked',
      )
      .dispatch({ current: true })
  }

  /** Cancel handler - discard payroll changes */
  public cancelClickHandler = (): void => {
    this.ecSg()
      .plugins.editingPlugin.feature.actions.discard.from(
        'Dialog.CancelButton.Clicked',
      )
      .dispatch({ current: true })
  }
}
